/** react-query hooks for every Moment API endpoint. Mutations invalidate the relevant list/detail queries. */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useApi } from '@/module/MomentProvider';
import type {
  AnalysisStatus,
  AnalyzeRequest,
  ApiKeyCreate,
  BulkAnalyzeRequest,
  CampaignCreate,
  CampaignPatch,
  CampaignStatus,
  CreativeCreate,
  CreativeUpdate,
  ExportCreate,
  ExportOut,
  JobListParams,
  MomentSearchParams,
  PublisherCreate,
  PublisherUpdate,
  PublisherVideoCreate,
  SegmentCreate,
  SegmentPreviewRequest,
  SegmentStatus,
  SegmentUpdate,
  SourceCreate,
  SourceOut,
  VideoListParams,
} from './types';

export const qk = {
  apiKeys: ['api-keys'] as const,
  sources: ['sources'] as const,
  source: (id: string) => ['sources', id] as const,
  videos: (params: VideoListParams) => ['videos', params] as const,
  video: (id: string) => ['video', id] as const,
  momentSearch: (params: MomentSearchParams) => ['moments', 'search', params] as const,
  moment: (id: string) => ['moments', id] as const,
  segments: (status?: SegmentStatus) => ['segments', status ?? 'all'] as const,
  segment: (id: string) => ['segment', id] as const,
  segmentMembers: (id: string) => ['segment-members', id] as const,
  segmentPreview: (body: SegmentPreviewRequest) => ['segment-preview', body] as const,
  webhookSecret: (id: string) => ['segment-webhook-secret', id] as const,
  exports: (segmentId?: string) => ['exports', segmentId ?? 'all'] as const,
  export: (id: string) => ['exports', 'one', id] as const,
  publishers: ['publishers'] as const,
  publisher: (id: string) => ['publishers', id] as const,
  publisherVideos: (id: string) => ['publisher-videos', id] as const,
  creatives: ['creatives'] as const,
  creative: (id: string) => ['creatives', id] as const,
  creativeTemplates: ['creative-templates'] as const,
  campaigns: (status?: CampaignStatus) => ['campaigns', status ?? 'all'] as const,
  campaign: (id: string) => ['campaign', id] as const,
  campaignStats: (id: string, days: number) => ['campaign-stats', id, days] as const,
  costs: (granularity: 'day' | 'month', days: number) => ['costs', granularity, days] as const,
  jobs: (params?: JobListParams) => ['jobs', params ?? {}] as const,
  job: (id: string) => ['jobs', 'one', id] as const,
};

// ---------- api keys ----------
export function useApiKeys() {
  const api = useApi();
  return useQuery({ queryKey: qk.apiKeys, queryFn: () => api.apiKeys.list() });
}
export function useCreateApiKey() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ApiKeyCreate) => api.apiKeys.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.apiKeys }),
  });
}
export function useRevokeApiKey() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.apiKeys.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.apiKeys }),
  });
}

// ---------- sources ----------
export function useSources() {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: qk.sources,
    queryFn: ({ pageParam }) => api.sources.list(pageParam, 100),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // Keep polling while a source is still discovering or has videos queued or analyzing, so the
    // "Running (n)" count on the sources table counts down live. Stops once nothing is active.
    refetchInterval: (q) =>
      q.state.data?.pages.some((p) => p.items.some(sourceActive)) ? SOURCES_POLL_MS : false,
  });
}
const SOURCES_POLL_MS = 5000;
const sourceActive = (s: SourceOut) =>
  s.status === 'pending' || s.status === 'discovering' || (s.active_count ?? 0) > 0;
export function useSource(id: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: qk.source(id ?? ''), queryFn: () => api.sources.get(id!), enabled: !!id });
}
export function useCreateSource() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SourceCreate) => api.sources.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sources }),
  });
}
export function useDiscoverSource() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.sources.discover(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sources }),
  });
}
export function useDeleteSource() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.sources.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.sources });
      void qc.invalidateQueries({ queryKey: ['videos'] });
    },
  });
}
export function useSourceEstimate() {
  const api = useApi();
  return useMutation({
    mutationFn: (args: { id: string; tier?: string; fps?: number | null; only_unanalyzed?: boolean }) =>
      api.sources.estimate(args.id, { tier: args.tier, fps: args.fps, only_unanalyzed: args.only_unanalyzed }),
  });
}

// ---------- videos ----------
export function useVideos(params: VideoListParams) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: qk.videos(params),
    queryFn: ({ pageParam }) => api.videos.list({ ...params, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}
export function useVideo(id: string | undefined, opts?: { refetchInterval?: number | false }) {
  const api = useApi();
  return useQuery({
    queryKey: qk.video(id ?? ''),
    queryFn: () => api.videos.get(id!),
    enabled: !!id,
    refetchInterval: (q) => {
      const st = q.state.data?.analysis_status;
      return st === 'queued' || st === 'analyzing' ? (opts?.refetchInterval ?? 5000) : false;
    },
  });
}
/**
 * Number of a source's videos in one analysis status, from the list endpoint's `total`.
 * Polls every 5 s while enabled; the bulk-analyze dialog uses it for its queued and running counters.
 */
export function useVideoStatusCount(sourceId: string | undefined, status: AnalysisStatus, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ['videos', 'count', sourceId ?? '', status] as const,
    queryFn: async () => (await api.videos.list({ source_id: sourceId, status, limit: 1 })).total ?? 0,
    enabled: !!sourceId && enabled,
    refetchInterval: enabled ? SOURCES_POLL_MS : false,
  });
}
function useInvalidateVideos() {
  const qc = useQueryClient();
  return (videoId?: string) => {
    void qc.invalidateQueries({ queryKey: ['videos'] });
    void qc.invalidateQueries({ queryKey: qk.sources });
    if (videoId) void qc.invalidateQueries({ queryKey: qk.video(videoId) });
  };
}
export function useAnalyzeVideo() {
  const api = useApi();
  const inv = useInvalidateVideos();
  return useMutation({
    mutationFn: (args: { id: string; body?: AnalyzeRequest }) => api.videos.analyze(args.id, args.body),
    onSuccess: (_d, v) => inv(v.id),
  });
}
export function useReanalyzeVideo() {
  const api = useApi();
  const inv = useInvalidateVideos();
  return useMutation({
    mutationFn: (args: { id: string; body?: AnalyzeRequest }) => api.videos.reanalyze(args.id, args.body),
    onSuccess: (_d, v) => inv(v.id),
  });
}
export function useBulkAnalyze() {
  const api = useApi();
  const inv = useInvalidateVideos();
  return useMutation({
    mutationFn: (body: BulkAnalyzeRequest) => api.videos.bulkAnalyze(body),
    onSuccess: () => inv(),
  });
}
export function useDeleteVideo() {
  const api = useApi();
  const inv = useInvalidateVideos();
  return useMutation({ mutationFn: (id: string) => api.videos.delete(id), onSuccess: () => inv() });
}

// ---------- moments ----------
export function useMomentSearch(params: MomentSearchParams, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: qk.momentSearch(params),
    queryFn: ({ signal }) => api.moments.search(params, signal),
    enabled,
    placeholderData: keepPreviousData,
  });
}
export function useMomentDetail(id: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: qk.moment(id ?? ''), queryFn: () => api.moments.get(id!), enabled: !!id });
}

// ---------- segments ----------
export function useSegments(status?: SegmentStatus) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: qk.segments(status),
    queryFn: ({ pageParam }) => api.segments.list({ status, cursor: pageParam, limit: 100 }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}
export function useSegment(id: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: qk.segment(id ?? ''), queryFn: () => api.segments.get(id!), enabled: !!id });
}
function useInvalidateSegments() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['segments'] });
    if (id) {
      void qc.invalidateQueries({ queryKey: qk.segment(id) });
      void qc.invalidateQueries({ queryKey: qk.segmentMembers(id) });
    }
  };
}
export function useCreateSegment() {
  const api = useApi();
  const inv = useInvalidateSegments();
  return useMutation({ mutationFn: (body: SegmentCreate) => api.segments.create(body), onSuccess: () => inv() });
}
export function useUpdateSegment() {
  const api = useApi();
  const inv = useInvalidateSegments();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: SegmentUpdate }) => api.segments.update(args.id, args.body),
    onSuccess: (_d, v) => {
      inv(v.id);
      if (v.body.regenerate_webhook_secret || v.body.webhook_url !== undefined) {
        void qc.invalidateQueries({ queryKey: qk.webhookSecret(v.id) });
      }
    },
  });
}
export function useDeleteSegment() {
  const api = useApi();
  const inv = useInvalidateSegments();
  return useMutation({ mutationFn: (id: string) => api.segments.delete(id), onSuccess: () => inv() });
}
export function useRecomputeSegment() {
  const api = useApi();
  const inv = useInvalidateSegments();
  return useMutation({ mutationFn: (id: string) => api.segments.recompute(id), onSuccess: (_d, id) => inv(id) });
}
export function useSegmentPreview(body: SegmentPreviewRequest | null) {
  const api = useApi();
  return useQuery({
    queryKey: qk.segmentPreview(body ?? { rules: { include: [], exclude: [] }, granularity: 'video', match_mode: 'any', min_confidence: 0 }),
    queryFn: ({ signal }) => api.segments.preview(body!, signal),
    enabled: !!body,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}
export function useSegmentMembers(id: string | undefined, limit = 100) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: qk.segmentMembers(id ?? ''),
    queryFn: ({ pageParam }) => api.segments.members(id!, pageParam, limit),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.next_cursor ? Number(last.next_cursor) : undefined),
    enabled: !!id,
  });
}
export function useWebhookSecret(id: string | undefined, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: qk.webhookSecret(id ?? ''),
    queryFn: () => api.segments.webhookSecret(id!),
    enabled: !!id && enabled,
    staleTime: 0,
  });
}

// ---------- exports ----------
const exportActive = (e: ExportOut) => e.status === 'queued' || e.status === 'running';
export function useExports(segmentId?: string) {
  const api = useApi();
  return useQuery({
    queryKey: qk.exports(segmentId),
    queryFn: () => api.exports.list({ segment_id: segmentId, limit: 100 }),
    refetchInterval: (q) => (q.state.data?.items.some(exportActive) ? 3000 : false),
  });
}
export function useCreateExport() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ExportCreate) => api.exports.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['exports'] }),
  });
}

// ---------- publishers ----------
export function usePublishers() {
  const api = useApi();
  return useQuery({ queryKey: qk.publishers, queryFn: () => api.publishers.list() });
}
export function useCreatePublisher() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PublisherCreate) => api.publishers.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.publishers }),
  });
}
export function useUpdatePublisher() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: PublisherUpdate }) => api.publishers.update(args.id, args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.publishers }),
  });
}
export function useRotatePublisherKey() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.publishers.rotateKey(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.publishers }),
  });
}
export function usePublisherVideos(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: qk.publisherVideos(id ?? ''),
    queryFn: () => api.publishers.videos(id!, 0, 500),
    enabled: !!id,
  });
}
export function useMapPublisherVideo() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: PublisherVideoCreate }) => api.publishers.mapVideo(args.id, args.body),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: qk.publisherVideos(v.id) });
      void qc.invalidateQueries({ queryKey: qk.publishers });
      void qc.invalidateQueries({ queryKey: ['videos'] });
    },
  });
}
export function useUnmapPublisherVideo() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; mappingId: string }) => api.publishers.unmapVideo(args.id, args.mappingId),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: qk.publisherVideos(v.id) });
      void qc.invalidateQueries({ queryKey: qk.publishers });
    },
  });
}

// ---------- creatives ----------
export function useCreatives() {
  const api = useApi();
  return useQuery({ queryKey: qk.creatives, queryFn: () => api.creatives.list() });
}
export function useCreative(id: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: qk.creative(id ?? ''), queryFn: () => api.creatives.get(id!), enabled: !!id });
}
export function useCreativeTemplates() {
  const api = useApi();
  return useQuery({ queryKey: qk.creativeTemplates, queryFn: () => api.creatives.templates(), staleTime: 300_000 });
}
export function useCreateCreative() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreativeCreate) => api.creatives.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.creatives }),
  });
}
export function useUpdateCreative() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: CreativeUpdate }) => api.creatives.update(args.id, args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.creatives }),
  });
}
export function useDeleteCreative() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.creatives.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.creatives }),
  });
}
export function useCreativePreview(body: CreativeCreate | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['creative-preview', body],
    queryFn: ({ signal }) => api.creatives.preview(body!, signal),
    enabled: !!body,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });
}

// ---------- campaigns ----------
export function useCampaigns(status?: CampaignStatus) {
  const api = useApi();
  return useQuery({ queryKey: qk.campaigns(status), queryFn: () => api.campaigns.list(status) });
}
export function useCampaign(id: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: qk.campaign(id ?? ''), queryFn: () => api.campaigns.get(id!), enabled: !!id });
}
export function useCreateCampaign() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CampaignCreate) => api.campaigns.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}
export function usePatchCampaign() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: CampaignPatch }) => api.campaigns.patch(args.id, args.body),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      void qc.invalidateQueries({ queryKey: qk.campaign(v.id) });
    },
  });
}
export function useDeleteCampaign() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.campaigns.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}
export function useCampaignStats(id: string | undefined, days: number) {
  const api = useApi();
  return useQuery({
    queryKey: qk.campaignStats(id ?? '', days),
    queryFn: () => api.campaigns.stats(id!, days),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}

// ---------- costs / jobs ----------
export function useCosts(granularity: 'day' | 'month', days: number) {
  const api = useApi();
  return useQuery({
    queryKey: qk.costs(granularity, days),
    queryFn: () => api.costs.get(granularity, days),
    placeholderData: keepPreviousData,
  });
}
export function useJobsList(params?: JobListParams, opts?: { enabled?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: qk.jobs(params),
    queryFn: () => api.jobs.list(params),
    enabled: opts?.enabled ?? true,
  });
}
export function useJob(id: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: qk.job(id ?? ''), queryFn: () => api.jobs.get(id!), enabled: !!id });
}
